<template>
	<b-modal ref="modal" :busy="isBusy" :hide-footer="pane === 0" :no-close-on-backdrop="hasMissingRequiredFields">
		<template #modal-header="{ close }">
			<div class="d-flex align-items-center w-100">
				<b-button v-if="canGoBack" variant="link" size="sm" class="p-0 mr-2 text-body" @click="goBack">
					<b-icon icon="arrow-left"></b-icon>
				</b-button>
				<h5 class="modal-title mb-0 flex-fill">{{modalTitle}}</h5>
				<button type="button" class="close ml-2" @click="close()"><span>&times;</span></button>
			</div>
		</template>

		<div class="pane-outer">
			<div class="pane-inner" :style="paneInnerStyle">
				<!-- Pane 0: Service select -->
				<div class="pane">
					<div class="text-center py-3" v-if="servicesLoading > 0">
						<b-icon icon="circle-fill" animation="throb" font-scale="3"></b-icon>
					</div>
					<b-alert variant="danger" :show="!!servicesError">{{servicesError}}</b-alert>
					<template v-if="servicesLoading === 0 && !servicesError">
						<b-input v-model="serviceFilter" placeholder="Search..." class="mb-2"/>
						<b-form-row cols-lg="4" align-v="stretch" style="max-height: 60vh; overflow-y: auto;">
							<b-col v-for="(service, idx) in filteredServices" :key="idx" align-self="stretch" class="mb-2">
								<b-button block class="h-100 position-relative" @click="openService(service, $event)">
									<b-badge v-if="service.upgradeRequired" variant="warning" class="position-absolute" style="top: 0.25rem; right: 0.25rem;">Upgrade</b-badge>
									<img v-if="service.icon_url" class="d-block mx-auto mb-1" :src="service.icon_url" style="max-width: 40px" />
									{{service.name}}
								</b-button>
							</b-col>
						</b-form-row>
					</template>
				</div>

				<!-- Pane 1: Search -->
				<div class="pane">
					<b-form-group description="Enter the name or the URL of your channel">
						<b-input-group>
							<b-form-input @input="channelSearch" v-model="channelSearchString" placeholder="Search..."></b-form-input>
							<b-input-group-append is-text v-if="searching > 0">
								<b-spinner small></b-spinner>
							</b-input-group-append>
						</b-input-group>
					</b-form-group>
					<div style="max-height: 260px; overflow-y: auto;" v-if="channelSuggestions.length">
						<b-list-group flush>
							<b-list-group-item v-for="(c, idx) in channelSuggestions" :key="idx" class="d-flex align-items-center px-2 py-2 channel-item">
								<b-avatar :src="c.thumbnail" size="2.5em" class="mr-3 flex-shrink-0"></b-avatar>
								<div class="flex-fill overflow-hidden mr-3">
									<div class="font-weight-bold text-truncate">{{c.name || c.token}}</div>
									<div class="text-muted small text-truncate" v-if="c.name">{{c.token}}</div>
								</div>
								<b-button variant="primary" size="sm" :disabled="saving > 0" @click="addChannel(c)">
									Add <b-icon icon="arrow-right"></b-icon>
								</b-button>
							</b-list-group-item>
						</b-list-group>
					</div>
					<b-alert variant="danger" :show="!!searchError" class="mt-2 mb-2">{{searchError}}</b-alert>
				</div>

				<!-- Pane 2: Edit -->
				<div class="pane">
					<div class="text-center py-3" v-if="editLoading > 0">
						<b-icon icon="circle-fill" animation="throb" font-scale="3"></b-icon>
					</div>
					<b-alert variant="danger" show v-else-if="!!editError">{{editError}}</b-alert>
					<b-form @submit.stop.prevent="save" id="channelEditForm" autocomplete="off" v-else>
						<b-avatar size="2em" :src="curChannel?.avatar_url||curChannel?.service_icon_url" badge-variant="" badge-offset="-0.4em">
							<template #badge><b-avatar v-if="curChannel?.avatar_url" :src="curChannel?.service_icon_url||null" size="1.3em" class="service_avatar" square :title="curChannel.service_name"></b-avatar></template>
						</b-avatar>
						{{curChannel.title}}
						<hr>
						<fieldset :disabled="saving > 0">
							<b-form-group label="Hide in">
								<b-form-checkbox v-model="curChannel.excludedFrom" value="viewer-count" switch>Viewer count widget</b-form-checkbox>
								<b-form-checkbox v-model="curChannel.excludedFrom" value="chat" switch>Chat widget</b-form-checkbox>
								<b-form-checkbox v-model="curChannel.excludedFrom" value="gift" switch>Alert widget</b-form-checkbox>
							</b-form-group>
							<b-form-group v-for="(field, i) in curChannel.protocolData?.channelSettingFields" :key="i" :label="(field.type != 'bool' && field.type != 'httpReq' && field.type != 'separator') ? `${field.label||i}${field.required?' (required)':' (optional)'}` : null" :description="(field.type != 'httpReq' && field.type != 'separator') ? field.description : null" :class="field.type == 'separator' ? 'mb-2' : null">
								<!-- separator: a divider with an optional section title -->
								<template v-if="field.type == 'separator'">
									<hr>
									<h6 v-if="field.title" class="mb-0">{{field.title}}</h6>
								</template>
								<!-- httpReq: a method + URL group (headers and body are their own fields) -->
								<template v-else-if="field.type == 'httpReq'">
									<div v-if="field.label" class="font-weight-bold">{{field.label}}</div>
									<p v-if="field.description" class="text-muted small mb-2">{{field.description}}</p>
									<b-input-group>
										<b-form-select :value="curChannel.data[i+'Method']||'GET'" :options="httpMethods" style="max-width: 6em" @input="v => setData(i+'Method', v)"></b-form-select>
										<b-form-input :value="curChannel.data[i+'Url']" placeholder="https://..." @input="v => setData(i+'Url', v)"></b-form-input>
									</b-input-group>
								</template>
								<key-value-list v-else-if="field.type == 'keyValueList'" :value="curChannel.data[i]" :disabled="isFieldDisabled(field)" @input="v => setData(i, v)"></key-value-list>
								<template v-else-if="field.type == 's3File'">
									<pipi-pupu-file-s3 v-if="curChannel.token" preview :accept="field.accept" :endpoint="`channel/${encodeURIComponent(curChannel.service_id)}/${encodeURIComponent(curChannel.token)}/${field.endpoint}`" :value="fileFieldValue(field, i)" @input="v => onFileField(field, i, v)"></pipi-pupu-file-s3>
									<b-form-text v-else>Save the channel first to upload.</b-form-text>
								</template>
								<b-form-textarea v-else-if="field.type == 'textarea'" v-model="curChannel.data[i]" rows="2" :disabled="isFieldDisabled(field)" :placeholder="field.placeholder"></b-form-textarea>
								<b-form-input v-else-if="field.type == 'int' || field.type == 'float'" v-model.number="curChannel.data[i]" type="number" :disabled="isFieldDisabled(field)" :placeholder="field.placeholder"></b-form-input>
								<password-input v-else-if="field.type == 'password'" v-model="curChannel.data[i]" :required="field.required"></password-input>
								<b-form-input v-else-if="field.type == 'color' || field.type == 'email'" v-model="curChannel.data[i]" :type="field.type" :required="field.required" :disabled="isFieldDisabled(field)"></b-form-input>
								<b-form-checkbox v-else-if="field.type == 'bool'" v-model="curChannel.data[i]" :value="true" :unchecked-value="false" :disabled="isFieldDisabled(field)">{{field.label||i}}</b-form-checkbox>
								<b-form-checkbox-group v-else-if="field.type == 'checkList'" v-model="curChannel.data[i]" :options="field.options" stacked :state="curChannel.data[i]?.length > 0 ? null : false" invalid-feedback="Please select at least one option." :required="field.required && !curChannel.data[i]?.length"/>
								<b-form-input v-else v-model="curChannel.data[i]" :disabled="isFieldDisabled(field)" :placeholder="field.placeholder"></b-form-input>
							</b-form-group>

							<!-- Membership tier prices. The tiers themselves (name, id) are read-only, learned
								from the platform; only the price is ever edited here, and saves immediately —
								it isn't part of the channel's main Save, since it's stored on its own table. -->
							<template v-if="curChannel.subscriptionLevels?.length > 0">
								<hr>
								<h6>Membership prices</h6>
								<p class="text-muted small mb-2">What each membership level costs. Levels are read from your channel automatically; the price is not — set it here so alerts and the gift goal can value a membership.</p>
								<b-form-group v-for="level in curChannel.subscriptionLevels" :key="level.level_id" :label="level.name">
									<b-input-group>
										<b-form-input v-model="level.currency" placeholder="USD" style="max-width: 6em" @change="saveSubscriptionLevel(level)"></b-form-input>
										<b-form-input v-model.number="level.value" type="number" step="0.01" min="0" placeholder="0.00" @change="saveSubscriptionLevel(level)"></b-form-input>
									</b-input-group>
								</b-form-group>
							</template>

							<!-- Membership emotes — every one requires joining at some level, but which
								level isn't tracked, so they're shown as one set rather than split per tier. -->
							<template v-if="membershipEmotes.length > 0">
								<hr>
								<h6>Membership emotes</h6>
								<div class="emote-grid">
									<img v-for="emote in membershipEmotes" :key="emote.emote_id" :src="emote.image_url" alt="" :aria-label="emote.label" :title="emote.shortcuts?.[0] || emote.label" loading="lazy" class="emote-icon" @error="onEmoteError" @load="onEmoteLoad">
								</div>
							</template>

							<!-- Non-membership channel emotes — not gated behind joining at all. -->
							<template v-if="nonMembershipEmotes.length > 0">
								<hr>
								<h6>Channel emotes</h6>
								<div class="emote-grid">
									<div v-for="emote in nonMembershipEmotes" :key="emote.emote_id" class="emote-item" :title="emote.shortcuts?.[0] || emote.label">
										<img :src="emote.image_url" alt="" :aria-label="emote.label" loading="lazy" class="emote-icon" @error="onEmoteError" @load="onEmoteLoad">
										<div class="text-muted emote-label">{{emote.label}}</div>
									</div>
								</div>
							</template>

							<!-- Custom bot: webhook (avatar, service icon + config come from channelSettingFields above).
								Other bots that just need the URL surfaced (e.g. BuyMeACoffee, whose own dashboard
								mints the signing secret) opt in with protocolData.channelWebhook instead — they get
								the URL box only; their secret is a plain channelSettingFields entry rendered above. -->
							<template v-if="curChannel.protocolData?.custom || curChannel.protocolData?.channelWebhook">
								<hr>
								<h6>Incoming webhook</h6>
								<p class="text-muted small mb-2" v-if="curChannel.protocolData?.custom">POST JSON events here to turn them into chat/gift messages. Send the secret as <code>Authorization: Bearer &lt;secret&gt;</code>.</p>
								<p class="text-muted small mb-2" v-else>Paste this URL into the platform's own webhook settings.</p>
								<b-form-group label="Webhook URL">
									<b-input-group>
										<b-input ref="webhookUrl" :value="curChannel.webhookUrl" readonly @focus="$event.target.select()"></b-input>
										<b-input-group-append>
											<b-button variant="outline-secondary" title="Copy" v-b-tooltip.hover @click="copyRef('webhookUrl')"><b-icon icon="clipboard"></b-icon></b-button>
										</b-input-group-append>
									</b-input-group>
								</b-form-group>
								<template v-if="curChannel.protocolData?.custom">
									<b-form-group label="Secret">
										<b-input-group>
											<b-input ref="webhookSecret" :type="secretVisible ? 'text' : 'password'" :value="curChannel.webhookSecret" readonly @focus="$event.target.select()"></b-input>
											<b-input-group-append>
												<b-button variant="outline-secondary" title="Show/Hide" v-b-tooltip.hover @click="secretVisible = !secretVisible"><b-icon :icon="secretVisible ? 'eye-slash' : 'eye'"></b-icon></b-button>
												<b-button variant="outline-secondary" title="Copy" v-b-tooltip.hover @click="copyRef('webhookSecret')"><b-icon icon="clipboard"></b-icon></b-button>
												<b-button variant="outline-secondary" title="Regenerate" v-b-tooltip.hover :disabled="regenerating" @click="regenerateWebhookSecret"><b-icon icon="arrow-repeat" :animation="regenerating ? 'spin' : null"></b-icon></b-button>
											</b-input-group-append>
										</b-input-group>
									</b-form-group>
									<b-form-group>
										<b-button size="sm" variant="primary" :disabled="testing" @click="sendTestWebhook"><b-spinner v-if="testing" small class="mr-1"></b-spinner>Send test event</b-button>
										<b-form-text v-if="testResult" :class="testResult.ok ? 'text-success' : 'text-danger'">{{ testResult.message }}</b-form-text>
									</b-form-group>
									<details class="mb-3">
										<summary class="small text-muted">Payload format</summary>
										<pre class="small mt-1"><code>{{ samplePayload }}</code></pre>
										<p class="small text-muted mb-0"><code>type</code>: chat | donation | follow | sub | raid (default chat). Gift fields are optional. You can also POST an array of events.</p>
									</details>
								</template>
							</template>

							<b-form-group label="Stream URL (optional)" v-if="user?.is_pro && !curChannel.protocolData?.noStreaming">
								<b-form-input v-model="curChannel.stream_url" autocomplete="off"></b-form-input>
							</b-form-group>
							<b-form-group label="Stream Key (optional)" v-if="user?.is_pro && !curChannel.protocolData?.noStreaming">
								<password-input v-model="curChannel.stream_key" autocomplete="new-password"></password-input>
							</b-form-group>
							<b-form-group label="Verify channel as yours (optional)" v-if="!curChannel.verified">
								<b-input-group>
									<b-input ref="verificationCommand" v-model="curChannel.verificationCommand" @focus="$event.target.select()"></b-input>
									<b-input-group-append>
										<b-button v-if="curChannel.verificationCommand" variant="outline-secondary" title="Copy to clipboard" v-b-tooltip.hover @click="copyVerificationCommandToClipboard"><b-icon icon="clipboard"></b-icon></b-button>
										<b-button variant="primary" title="Generate" v-b-tooltip.hover @click="generateVerificationCommand">Generate</b-button>
									</b-input-group-append>
									<p class="text-muted">Without verification, you cannot use commands, timers and bridging in this channel. The bot will not send any messages to it.</p>
								</b-input-group>
								<b-form-text>{{curChannel.verificationCommandMessage}}</b-form-text>
							</b-form-group>
							<b-form-group label="Bridging" v-if="curChannel.verified && user?.bridging_status == 'enabled'">
								<b-form-checkbox v-if="!curChannel.protocolData?.readOnly" v-model="curChannel.bridging_no_receive">Don't receive messages from other platforms</b-form-checkbox>
								<b-form-checkbox v-model="curChannel.bridging_no_send">Don't send messages to other platforms</b-form-checkbox>
							</b-form-group>
						</fieldset>
					</b-form>
				</div>
			</div>
		</div>

		<template #modal-footer="{ cancel }">
			<template v-if="pane === 1">
				<b-button size="sm" @click="cancel()">Cancel</b-button>
			</template>
			<div class="w-100" v-else-if="pane === 2">
				<b-alert variant="danger" :show="!!savingError">{{savingError}}</b-alert>
				<div class="float-left">
					<a href="#" @click.prevent.stop="showDeleteModal()" class="text-danger"><b-icon icon="trash"></b-icon> Delete</a>
				</div>
				<div class="float-right">
					<b-spinner v-show="saving > 0" small label="Spinning" class="mr-3"></b-spinner>
					<b-button size="sm" class="mr-2" v-if="curChannel.auth" @click.prevent="openAuthPopup(curChannel.auth.url)" :href="curChannel.auth.url" target="_blank">Reauthenticate <b-icon icon="arrow-up-right-square-fill"></b-icon></b-button>
					<b-button size="sm" variant="success" type="submit" form="channelEditForm" class="mr-2">Save</b-button>
					<b-button size="sm" @click="cancel()">Cancel</b-button>
				</div>
			</div>
		</template>

		<channel-delete-modal ref="channelDeleteModal" @deleted="$refs.modal.hide(); $emit('saved')"></channel-delete-modal>
	</b-modal>
</template>

<script>
import debounce from 'debounce';
import PasswordInput from '../../PasswordInput.vue';
import PipiPupuFileS3 from '../../PipiPupuFileS3.vue';
import ChannelDeleteModal from './Delete';
import KeyValueList from './KeyValueList.vue';

export default {
	name: 'ChannelModal',
	components: { PasswordInput, PipiPupuFileS3, ChannelDeleteModal, KeyValueList },
	data() { return {
		httpMethods: ['GET', 'POST'],
		secretVisible: false,
		regenerating: false,
		testing: false,
		testResult: null,
		pane: 0,
		isAddFlow: false,

		// Pane 0 (service select)
		servicesLoading: 0,
		servicesError: null,
		services: [],
		serviceFilter: '',

		// Pane 0→1
		serviceId: null,
		serviceName: null,

		// Pane 1 (search)
		channelSearchString: '',
		channelSuggestions: [],
		searching: 0,
		searchError: null,
		saving: 0,

		// Pane 2 (edit)
		editLoading: 0,
		editError: null,
		savingError: null,
		user: null,
		channelToken: null,
		curChannel: { title: '', token: '', data: {}, flags: [] },
	}},
	computed: {
		modalTitle() {
			if(this.pane === 0) return 'Add a channel';
			if(this.pane === 1) return this.serviceName ? `Add ${this.serviceName} channel` : 'Add a channel';
			return this.isAddFlow ? 'Configure channel' : 'Edit channel';
		},
		canGoBack() {
			return this.pane === 1 || (this.pane === 2 && this.isAddFlow);
		},
		isBusy() {
			if(this.pane === 0) return this.servicesLoading > 0;
			if(this.pane === 1) return this.saving > 0;
			return this.editLoading > 0;
		},
		// Some services have mandatory settings that must be filled in before the channel does anything
		// (e.g. Discord's channel list, which is why the auth flow opens this modal). While one of those
		// is still empty, losing the modal to a stray click on the backdrop is worse than the
		// convenience, so only the header X, Cancel and Esc close it.
		hasMissingRequiredFields() {
			if(this.pane !== 2) return false;
			return Object.entries(this.curChannel.protocolData?.channelSettingFields || {})
				.some(([ name, field ]) => field.required && !this.isFieldDisabled(field) && !this.isFieldFilled(field, name));
		},
		filteredServices() {
			const f = this.serviceFilter.toLowerCase();
			return f ? this.services.filter(s => s.name.toLowerCase().startsWith(f)) : this.services;
		},
		paneInnerStyle() {
			return { transform: `translateX(calc(-100% * ${this.pane} / 3))` };
		},
		samplePayload() {
			return JSON.stringify({
				type: 'gift',
				username: 'bob',
				displayName: 'Bob',
				avatarUrl: 'https://example.com/bob.png',
				text: 'nice stream!',
				giftName: 'Rocket',
				giftValue: 4.99,
				currency: 'USD',
				id: 'abc123',
			}, null, 2);
		},
		membershipEmotes() {
			return (this.curChannel.emotes || []).filter(e => e.is_members);
		},
		nonMembershipEmotes() {
			return (this.curChannel.emotes || []).filter(e => !e.is_members);
		},
	},
	mounted() { window.addEventListener('message', this.onOpenEditMessage); },
	beforeDestroy() { window.removeEventListener('message', this.onOpenEditMessage); },
	methods: {
		onEmoteError(e) { e.target.classList.add('emote-missing'); },
		onEmoteLoad(e) { e.target.classList.remove('emote-missing'); },
		// Public API
		show() {
			this.pane = 0;
			this.isAddFlow = false;
			this.serviceFilter = '';
			this.channelSuggestions = [];
			this.searchError = null;
			this.$refs.modal.show();
			this.loadServices();
		},
		showEdit(serviceId, channelToken) {
			this.pane = 2;
			this.isAddFlow = false;
			this.serviceId = serviceId;
			this.channelToken = channelToken;
			this.curChannel = {
				title: '',
				token: '',
				data: {},
				flags: [],
				excludedFrom: [],
				verificationCommand: '',
				verificationCommandMessage: '',
			};
			this.$refs.modal.show();
			this.loadEditData();
		},

		goBack() {
			this.pane--;
		},

		// Pane 0
		async loadServices() {
			try {
				this.servicesLoading++;
				this.servicesError = null;
				const all = await this.$api.get('services');
				this.services = all.filter(s => s.capabilities.includes('chat'));
			} catch(e) {
				this.servicesError = e?.error || e;
			} finally {
				this.servicesLoading--;
			}
		},

		openService(service, event) {
			if(service.upgradeRequired) {
				this.$refs.modal.hide();
				this.$root.$emit('show-upgrade-modal');
				return;
			}
			if(service.auth && service.auth.url && !event?.shiftKey) {
				window.open(service.auth.url, 'Authenticate', `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=${service.auth.width||700},height=${service.auth.height||600}`);
				this.$refs.modal.hide();
				return;
			}
			this.serviceId = service.id;
			this.serviceName = service.name;
			this.channelSearchString = '';
			this.channelSuggestions = [];
			this.searchError = null;
			this.pane = 1;
		},

		// Pane 1
		channelSearch: debounce(function() {
			if(!this.serviceId) return;
			this.searching++;
			this.channelSuggestions = [];
			this.searchError = null;
			this.$api
				.get(`channels/${encodeURIComponent(this.serviceId)}/search`, { q: this.channelSearchString })
				.then(results => {
					this.channelSuggestions = results.map(r => ({ ...r, title: r.name||r.token }));
				}).catch(err => {
					console.error('Channel search', err);
					this.channelSuggestions = [];
					this.searchError = err?.error||err;
				}).finally(() => {
					this.searching--;
				});
		}, 300),
		async addChannel(item) {
			// OAuth-on-add services (e.g. Shopify) carry a ready-to-open authorize URL — clicking opens the
			// OAuth flow and the channel is created from the callback, instead of being created here.
			if(item.authUrl) {
				window.open(item.authUrl, 'Authenticate', 'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=700,height=600');
				this.$refs.modal.hide();
				return;
			}
			try {
				this.saving++;
				this.searchError = null;
				await this.$api.post('channel', { data: {
					serviceId: this.serviceId,
					token: item.token,
					title: item.title,
					avatar_url: item.thumbnail,
					data: item.data||{},
				}});
				this.$emit('saved');
				this.channelToken = item.token;
				this.isAddFlow = true;
				this.curChannel = {
					title: '',
					token: '',
					data: {},
					flags: [],
					verificationCommand: '',
					verificationCommandMessage: '',
				};
				this.pane = 2;
				this.loadEditData();
			} catch(e) {
				this.searchError = e?.error||e;
			} finally {
				this.saving--;
			}
		},

		// Pane 2
		async loadEditData() {
			try {
				this.editLoading++;
				this.editError = null;
				this.savingError = null;
				const [, channel, chatSettings, giftSettings, vcSettings] = await Promise.all([
					this.$api.get('userData').then(u => { this.user = u; }),
					this.$api.get(`channel/${encodeURIComponent(this.serviceId)}/${encodeURIComponent(this.channelToken)}`),
					this.$api.get('widgetSettings/chat').catch(() => ({})),
					this.$api.get('widgetSettings/gift').catch(() => ({})),
					this.$api.get('widgetSettings/viewer-count').catch(() => ({})),
				]);
				const key = `${channel.service_id}:${channel.token}`;
				this.curChannel = {
					verificationCommand: '',
					verificationCommandMessage: '',
					...channel,
					data: channel.data || {},
					flags: channel.flags || [],
					excludedFrom: [
						...(chatSettings.excludedChannels?.includes(key) ? ['chat'] : []),
						...(giftSettings.excludedChannels?.includes(key) ? ['gift'] : []),
						...(vcSettings.excludedChannels?.includes(key) ? ['viewer-count'] : []),
					],
				};
			} catch(e) {
				console.error('Loading edit data', e.stack);
				this.editError = String(e.error||e.message||e);
			} finally {
				this.editLoading--;
			}
		},
		async save() {
			try {
				this.saving++;
				this.savingError = null;
				await this.$api.patch(`channel/${encodeURIComponent(this.serviceId)}/${encodeURIComponent(this.channelToken)}`, {
					data: this.curChannel.data,
					...(this.curChannel.protocolData?.custom ? { service_icon_url: this.curChannel.service_icon_override ?? null } : {}),
					stream_url: this.curChannel.stream_url,
					stream_key: this.curChannel.stream_key,
					flags: this.curChannel.flags || [],
					excludedFrom: this.curChannel.excludedFrom || [],
				});
				this.$refs.modal.hide();
				this.$bvToast.toast('Successfully updated channel', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
				this.$emit('saved');
			} catch(e) {
				this.savingError = e;
			} finally {
				this.saving--;
			}
		},
		openAuthPopup(authUrl) {
			window.open(authUrl, 'Authenticate', 'scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,width=600,height=500');
		},
		async generateVerificationCommand() {
			try {
				this.loading++;
				const result = await this.$api.get(`channel/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/verificationCode`);
				this.curChannel.verificationCommand = `!verify ${result.code}`;
				this.curChannel.verificationCommandMessage = `Post this command, as the channel owner, in your chat. Expires in ${result.expiryMinutes} minutes.`;
			} catch(e) {
				console.error('Generating verification command', e);
				this.error = e.error||e;
			} finally {
				this.loading--;
			}
		},
		copyVerificationCommandToClipboard() {
			this.$refs.verificationCommand.focus();
			document.execCommand('copy');
		},
		setData(key, val) {
			this.$set(this.curChannel.data, key, val);
		},
		async saveSubscriptionLevel(level) {
			try {
				await this.$api.post(`channel/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/subscriptionLevel/${encodeURIComponent(level.level_id)}`, {
					currency: level.currency || null,
					value: level.value || null,
				});
			} catch(e) {
				console.error('Saving membership level price', e);
				this.savingError = e;
			}
		},
		isFieldDisabled(field) {
			// `requireField` gates a field on another data field (e.g. a URL) being set;
			// `postOnlyField` gates it on the named method field being POST (e.g. a request body).
			if(field.requireField && !this.curChannel.data[field.requireField]) return true;
			if(field.postOnlyField && (this.curChannel.data[field.postOnlyField] || 'GET') !== 'POST') return true;
			return false;
		},
		isFieldFilled(field, name) {
			// Field values live in `curChannel.data` under the field name, except `httpReq` (which keeps
			// its URL in <name>Url) and `s3File` fields bound to a channel column (e.g. icon_url).
			// `false` and `0` are answers; an empty string, list or object is not.
			const value = field.type == 'httpReq' ? this.curChannel.data[`${name}Url`]
				: field.column ? this.curChannel[field.column]
				: this.curChannel.data[name];
			if(Array.isArray(value)) return value.length > 0;
			if(value && typeof value == 'object') return Object.keys(value).length > 0;
			return value != null && value !== '';
		},
		copyRef(ref) {
			const el = this.$refs[ref];
			if(!el) return;
			el.focus();
			el.select?.();
			document.execCommand('copy');
			this.$bvToast.toast('Copied to clipboard', { variant: 'success', solid: true, noCloseButton: true, toaster: 'b-toaster-bottom-right' });
		},
		fileFieldValue(field, key) {
			// `column` stores a bare URL on the channel (e.g. icon_url); otherwise store the file object in data.
			if(field.column) {
				const url = this.curChannel[field.column];
				return url ? { url } : null;
			}
			return this.curChannel.data[key] || null;
		},
		onFileField(field, key, file) {
			// Uploads go to MinIO and return a URL; we store it (in a channel field or in data) and
			// persist on Save. `column` writes a top-level channel field; otherwise store in data.
			if(field.column) {
				this.$set(this.curChannel, field.column, file?.url || null);
			} else {
				this.setData(key, file || null);
			}
		},
		async regenerateWebhookSecret() {
			try {
				this.regenerating = true;
				const { secret } = await this.$api.post(`channel/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/webhookSecret`, {});
				this.curChannel = { ...this.curChannel, webhookSecret: secret };
			} catch(e) {
				this.$bvToast.toast(e?.error || e?.message || String(e), { title: 'Error', variant: 'danger', solid: true });
			} finally {
				this.regenerating = false;
			}
		},
		async sendTestWebhook() {
			try {
				this.testing = true;
				this.testResult = null;
				const result = await this.$api.post(`channel/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/testWebhook`, {});
				this.testResult = { ok: !!result?.ok, message: result?.ok ? 'Test event sent — check your chat/alert widget.' : (result?.error || 'Failed') };
			} catch(e) {
				console.error(e);
				this.testResult = { ok: false, message: e?.error || e?.message || String(e) };
			} finally {
				this.testing = false;
			}
		},
		showDeleteModal() {
			this.$refs.channelDeleteModal.show(this.serviceId, this.channelToken, this.curChannel?.service_name, this.curChannel?.protocol_name);
		},
		onOpenEditMessage(event) {
			const base = (process.env.VUE_APP_PIPIPUPUBASE || location.origin).replace(/\/$/, '');
			if(event.origin !== base) return;
			if(event.data?.type != 'openChannelEdit') return;
			this.showEdit(event.data.service_id, event.data.token, { waitForChannel: true });
		},
	},
};
</script>

<style scoped lang="scss">
@import '@/plugins/variable.scss';

.pane-outer {
	overflow: hidden;
}

.pane-inner {
	display: flex;
	width: 300%;
	transition: transform 0.3s ease;

	.pane {
		flex: 0 0 calc(100% / 3);
		min-width: 0;
		min-height: 200px;
	}
}

.channel-item {
	background-color: $gray-200;

	&:nth-child(odd) {
		background-color: $gray-400;
	}
}

.emote-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 0.25rem;
}

// Sized in rem, not em: an empty alt means there's no fallback text to worry
// about sizing against, but the box shouldn't shift if that ever changes.
.emote-icon {
	width: 2rem;
	height: 2rem;
	object-fit: contain;
}

// A failed load (e.g. a 429 from Google) is otherwise just empty space —
// onEmoteError marks it so there's still something to see.
.emote-icon.emote-missing {
	box-sizing: border-box;
	border: 1px solid rgba(255, 255, 255, 0.25);
	border-radius: 0.25rem;
	background: rgba(255, 255, 255, 0.08);
}

.emote-item {
	width: 3em;
	text-align: center;
}

.emote-label {
	font-size: 0.7em;
	overflow-wrap: break-word;
}
</style>
